import React, { useCallback, useEffect, useRef, useState } from 'react';

import { getProcessedAdsText, getSellerIdToUse } from '../../../../utils/ads-texts-helpers';
import TitleAndDescription from '../../title-and-description';
import { useTenantConfig } from '../../../../tenant-config/useTenantConfig';
import { APP_ADS_TEXT } from '../../../../tenant-config/tenantConfigConstants';

interface AppAdsTextGeneratorProps {
  spotId: string;
  isSellerIdFetched: boolean;
  sellerId: string;
  aylSellerId: string;
}

const AppAdsTextGenerator = (props: AppAdsTextGeneratorProps) => {
  const { isSellerIdFetched, spotId, sellerId, aylSellerId } = props;
  const [appAdsTxtConfiguration, isAppAdsTxtConfigurationLoading] = useTenantConfig(APP_ADS_TEXT);
  const appAdsTextareaElement = useRef(null);
  const [appAdsText, setAppAdsText] = useState('');
  const [isTextFetching, setIsTextFetching] = useState(false);
  const [isTextVisible, setIsTextVisible] = useState(false);

  useEffect(() => {
    if (isAppAdsTxtConfigurationLoading || !isSellerIdFetched || !appAdsTxtConfiguration) {
      return;
    }

    const fetchAppAdsText = async () => {
      try {
        const {
          url: appAdsTxtUrl,
          sellerIdOverrides: appAdsTxtSellerIdOverrides = []
        } = appAdsTxtConfiguration;

        setIsTextFetching(true);

        const appAdsTextTemplateResponse = await fetch(appAdsTxtUrl);
        const appAdsTextTemplate = await appAdsTextTemplateResponse.text();
        const appAdsSellerIdToUse = getSellerIdToUse(sellerId, appAdsTxtSellerIdOverrides, spotId);
        const processedAppAdsText = getProcessedAdsText(
          appAdsTextTemplate,
          appAdsSellerIdToUse,
          aylSellerId
        );

        setAppAdsText(processedAppAdsText);
        setIsTextFetching(false);
      } catch (err) {
        console.error('Failed to fetch app ads text', err);
      }
    };

    fetchAppAdsText();
  }, [
    appAdsTxtConfiguration,
    aylSellerId,
    isAppAdsTxtConfigurationLoading,
    isSellerIdFetched,
    sellerId,
    spotId
  ]);

  const onGetAppAdsTextClick = useCallback(() => {
    setIsTextVisible(true);
    appAdsTextareaElement.current.value = appAdsText;
  }, [appAdsText]);

  return (
    <div>
      <TitleAndDescription
        title={
          <div>
            <div className="ads-text-title">
              App Ads.txt
              <div className="ads-text-badge">IMPORTANT</div>
            </div>
          </div>
        }
        description={
          <div className="ads-text-last-update">
            {!isTextFetching && appAdsTxtConfiguration && (
              <b>Last updated {appAdsTxtConfiguration.updateDate}</b>
            )}
          </div>
        }
      />
      <button
        className="ads-text-copy-button"
        disabled={isAppAdsTxtConfigurationLoading || isTextFetching || !appAdsText}
        onClick={onGetAppAdsTextClick}
      >
        Get App Ads.txt
      </button>

      <textarea className={isTextVisible ? 'isVisible' : ''} ref={appAdsTextareaElement} />
    </div>
  );
};

export default AppAdsTextGenerator;
